// ─────────────────────────────────────────────
//  Coverage Map panel — how many rooms this facility needs staffed, per time
//  block, per weekday.
//
//  Five FIXED blocks, the same for every facility (7a-3p, 3p-5p, 5p-7p, 7p-9p,
//  9p-7a). Only the numbers are per-facility, so this panel edits numbers and
//  nothing else — no adding, removing or re-timing blocks.
//
//  Read by exactly one thing: the Timeline Chart's Staffing Adequacy section,
//  which compares providers on shift against these numbers and reports
//  Overcovered / Adequate / Undercovered.
//
//  Admin-only (facilities update rule; deliberately NOT in the AIC field
//  allowlist — an AIC runs a day, they do not renegotiate coverage).
//
//  Lives in its own file because setupPage.facility.js is at the 500-line
//  ceiling in CLAUDE.md. Templated on FacilityRulesPanel (setupPage.rules.js).
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateFacility } from "../firebase/firestore";
import { useFacility } from "../hooks/useFacility";
import { COVERAGE_BLOCKS } from "../domain/coverageMap";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

const DAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
// Monday-first for EDITING, because that is how the printed coverage sheet
// reads. `WEEKDAY_KEYS` in domain/coverageMap.js stays in Date.getDay() order
// (Sunday first) so lookups need no arithmetic. Two orders on purpose — one for
// the eye, one for the index. Do not "tidy" them together.
const EDIT_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// Stored map → editable strings, one row per fixed block.
//
// "" is the EMPTY cell and is NOT zero. Blank means NO NUMBER SET (Staffing
// Adequacy reports "no coverage map set" and falls back to its old maths); a
// typed 0 means this campus genuinely needs no rooms covered in that block,
// which is a real answer overnight and at weekends. Both survive the round trip
// separately, and `Number(null) === 0` is exactly the collapse being avoided.
function toRows(stored) {
  return COVERAGE_BLOCKS.map((b) => ({
    id: b.id,
    label: b.label,
    rooms: Object.fromEntries(
      EDIT_DAYS.map((d) => {
        const v = stored?.[b.id]?.[d];
        return [d, Number.isFinite(v) ? String(v) : ""];
      })
    ),
  }));
}

// Rows → the stored map. A block with no numbers at all is omitted entirely
// rather than written as an empty object, so "never configured" stays legible in
// the document itself.
export function rowsToStored(rows) {
  const out = {};
  for (const r of rows || []) {
    const day = {};
    for (const d of EDIT_DAYS) {
      const raw = String(r.rooms?.[d] ?? "").trim();
      if (raw === "") continue; // blank stays ABSENT — not 0
      const n = Number(raw);
      if (Number.isInteger(n) && n >= 0 && n <= 99) day[d] = n;
    }
    if (Object.keys(day).length) out[r.id] = day;
  }
  return out;
}

export function CoverageMapPanel({ facilityId }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { facility, loading } = useFacility(facilityId);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  // ⚠️ Keyed on PRIMITIVES, not on `facility`. A fresh object identity on every
  // snapshot would reset the form mid-edit, and an unstable object in an effect's
  // deps is what OOM-crashed a vitest worker in this repo before (see the
  // vi.hoisted() note in setupPage.facility.sandbox.test.js). The stored map is
  // read INSIDE the effect from its serialised form so the identity churn cannot
  // reach the deps array at all.
  const storedKey = facility?.coverageMap ? JSON.stringify(facility.coverageMap) : "";
  useEffect(() => {
    setRows(toRows(storedKey ? JSON.parse(storedKey) : null));
  }, [facilityId, storedKey]);

  if (loading || !rows) return null;

  const patch = (i, day, value) =>
    setRows((p) => p.map((r, idx) => (idx === i ? { ...r, rooms: { ...r.rooms, [day]: value } } : r)));

  const save = async () => {
    setSaving(true);
    try {
      await updateFacility(facilityId, { coverageMap: rowsToStored(rows) });
      toast.success("Coverage map saved — Staffing Adequacy reads it immediately.");
    } catch (err) {
      console.error("Save coverage map failed:", err);
      toast.error("Could not save. Admin rights are required for coverage settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div style={s.panelTitle}>🗓️ Coverage Map</div>
        {isAdmin && (
          <button style={s.primaryBtn} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Coverage"}
          </button>
        )}
      </div>
      <p style={{ fontSize: 12, color: color.text.muted, marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
        How many rooms this facility needs staffed in each time block, by weekday.
        The Timeline Chart's <strong>Staffing Adequacy</strong> section compares the
        providers on shift against these numbers and reports Overcovered, Adequate,
        or Undercovered. The five blocks are the same at every facility; only the
        numbers change here.
      </p>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
          <thead>
            <tr>
              <th style={th}>Time block</th>
              {EDIT_DAYS.map((d) => (
                <th key={d} style={{ ...th, textAlign: "center", width: 58 }}>{DAY_LABEL[d]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id}>
                <th scope="row" style={{ ...td, fontWeight: 700, whiteSpace: "nowrap", textAlign: "left" }}>
                  {r.label}
                </th>
                {EDIT_DAYS.map((d) => (
                  <td key={d} style={{ ...td, textAlign: "center" }}>
                    <input
                      type="number" min="0" max="99" style={numInput} disabled={!isAdmin}
                      value={r.rooms[d]}
                      placeholder="—"
                      onChange={(e) => patch(i, d, e.target.value)}
                      aria-label={`${r.label} ${DAY_LABEL[d]} rooms to cover`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Blank vs 0 is the distinction the feature rests on, so it is spelled out
          next to the cells rather than buried in a tooltip. */}
      <div style={{ fontSize: 11.5, color: color.text.muted, marginTop: 10, lineHeight: 1.55 }}>
        Leave a cell <strong>blank</strong> when no number is set — Staffing Adequacy
        keeps using its existing rooms-running calculation for that block. Type{" "}
        <strong>0</strong> when this campus genuinely needs no rooms covered then;
        the board reads that as a real zero. A blank and a zero are not the same thing.
      </div>
    </div>
  );
}

// ⚠️ fontSize 16 IS THE MINIMUM on every focusable field, and the repo has a test
// that enforces it (`iOS input zoom guard`). Safari zooms the whole page when a
// focused input is under 16px, and the symptom looks like the page drifting side
// to side — which sends you hunting for an overflow that is not there.
const th = { textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: ".5px", color: color.text.muted, padding: "0 6px 7px", whiteSpace: "nowrap" };
const td = { padding: "5px 6px", borderTop: `1px solid ${color.border.default}` };
const numInput = { width: 54, fontSize: 16, padding: "5px 4px", textAlign: "center", border: `1px solid ${color.border.default}`, borderRadius: 6, background: color.surface.card, color: color.text.primary, fontFamily: "inherit" };

export default CoverageMapPanel;
